/**
 * What's new (Vincent, 2026-11-01): Crewdle's product news as RELEASES — the launch release (2026-09-04, "Meet the
 * new Crewdle"), then one per week with its highlights ("an initial release, then one per week with the highlights").
 * Releases live once in the global project's `releases` (firestore.rules; written by Crewdle's AI agents with
 * backend scripts/whats-new.mjs) and are read there by every Connect. Each HIGHLIGHT carries its own gate —
 * audience (admins), per-org feature flags (memory-config), enabled products, region, plan — checked against the
 * person's OPEN org (whatsNewContext, in the org's region). A release shows only the highlights that apply; one
 * with none is hidden and never lights the dot. Pure helpers here, so they can move to the kit with the list.
 */

export interface ReleaseText {
  title: string;
  /** Release: an optional one-line intro. Highlight: its 1–2 sentences. */
  body: string;
}

export interface Highlight {
  id: string;
  en: ReleaseText;
  fr: ReleaseText | null;
  audience: 'all' | 'admins';
  imageUrl: string | null;
  tryIt: { route: string; query: Record<string, string> } | null;
  requires: {
    memoryFlags: string[];
    productAccess: string[];
    regions: string[];
    plan: 'paid' | null;
  };
}

export interface WhatsNewRelease {
  id: string;
  kind: 'launch' | 'weekly';
  /** Monday of the release's week (YYYY-MM-DD); the launch date for the launch release. */
  weekOf: string;
  en: ReleaseText;
  fr: ReleaseText | null;
  publishedAt: number;
  products: string[];
  highlights: Highlight[];
}

/** The open org's capabilities (functions/app whatsNewContext). */
export interface WhatsNewContext {
  admin: boolean;
  paid: boolean;
  region: string;
  flags: Record<string, boolean>;
  products: string[];
}

/** "Try it" goes only to an in-app route (same allowlist as scripts/whats-new.mjs and firestore.rules). */
export const TRY_IT_ROUTE = /^\/(agents|projects|tasks|messaging|chat|marketplace|memory|local-models|account|teams)(\/[A-Za-z0-9_-]+)*$/;
const IMAGE_URL = /^https:\/\/(firebasestorage\.googleapis\.com|storage\.googleapis\.com)\//;

const strings = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && !!x) : []);
const millis = (v: unknown): number =>
  typeof v === 'number' ? v : ((v as { toMillis?: () => number } | null | undefined)?.toMillis?.() ?? 0);

function text(v: unknown, needsBody: boolean): ReleaseText | null {
  const t = (v ?? {}) as Record<string, unknown>;
  const title = typeof t.title === 'string' ? t.title.trim() : '';
  const raw = typeof t.body === 'string' ? t.body : typeof t.intro === 'string' ? t.intro : '';
  const body = raw.trim();
  if (!title || (needsBody && !body)) return null;
  return { title, body };
}

function toHighlight(raw: unknown, index: number): Highlight | null {
  const h = (raw ?? {}) as Record<string, unknown>;
  const en = text(h.en, true);
  if (!en) return null;
  const req = (h.requires ?? {}) as Record<string, unknown>;
  const tryRaw = (h.tryIt ?? null) as { route?: unknown; query?: unknown } | null;
  const route = typeof tryRaw?.route === 'string' && TRY_IT_ROUTE.test(tryRaw.route) ? tryRaw.route : null;
  const query: Record<string, string> = {};
  if (tryRaw?.query && typeof tryRaw.query === 'object') {
    for (const [k, v] of Object.entries(tryRaw.query as Record<string, unknown>)) {
      if (/^[A-Za-z0-9_-]{1,40}$/.test(k) && typeof v === 'string' && v.length <= 200) query[k] = v;
    }
  }
  return {
    id: typeof h.id === 'string' && /^[A-Za-z0-9_-]{1,80}$/.test(h.id) ? h.id : `h${index}`,
    en,
    fr: text(h.fr, true),
    audience: h.audience === 'admins' ? 'admins' : 'all',
    imageUrl: typeof h.imageUrl === 'string' && IMAGE_URL.test(h.imageUrl) ? h.imageUrl : null,
    tryIt: route ? { route, query } : null,
    requires: {
      memoryFlags: strings(req.memoryFlags),
      productAccess: strings(req.productAccess),
      regions: strings(req.regions),
      plan: req.plan === 'paid' ? 'paid' : null,
    },
  };
}

/** Firestore doc → release, or null when it is not a usable published release with at least one highlight. */
export function toRelease(id: string, raw: Record<string, unknown> | undefined): WhatsNewRelease | null {
  if (!raw || raw.status !== 'published') return null;
  const en = text(raw.en, false);
  const publishedAt = millis(raw.publishedAt);
  if (!en || !publishedAt) return null;
  const highlights = (Array.isArray(raw.highlights) ? raw.highlights : [])
    .map((h, i) => toHighlight(h, i)).filter((h): h is Highlight => !!h);
  if (!highlights.length) return null;
  return {
    id,
    kind: raw.kind === 'launch' ? 'launch' : 'weekly',
    weekOf: typeof raw.weekOf === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw.weekOf) ? raw.weekOf : '',
    en,
    fr: text(raw.fr, false),
    publishedAt,
    products: strings(raw.products),
    highlights,
  };
}

/** Connect is always on for an org; other products need their enabledProducts entry. */
const ALWAYS_ON = new Set(['connect']);

/** Does this highlight apply to the person in their open org? */
export function highlightVisible(h: Highlight, ctx: WhatsNewContext): boolean {
  if (h.audience === 'admins' && !ctx.admin) return false;
  if (h.requires.memoryFlags.some((f) => ctx.flags[f] !== true)) return false;
  if (h.requires.productAccess.some((p) => !ALWAYS_ON.has(p) && !ctx.products.includes(p))) return false;
  if (h.requires.regions.length && !h.requires.regions.includes(ctx.region)) return false;
  if (h.requires.plan === 'paid' && !ctx.paid) return false;
  return true;
}

/**
 * The releases this person sees, newest first, each with only the highlights that apply to their open org.
 * A release with none left is dropped. Unknown capabilities (not loaded yet) show nothing.
 */
export function visibleReleases(all: WhatsNewRelease[], ctx: WhatsNewContext | null, product: string, nowMs: number): WhatsNewRelease[] {
  if (!ctx) return [];
  return all
    .filter((r) => r.products.includes(product) && r.publishedAt <= nowMs)
    .map((r) => ({ ...r, highlights: r.highlights.filter((h) => highlightVisible(h, ctx)) }))
    .filter((r) => r.highlights.length > 0)
    .sort((a, b) => b.publishedAt - a.publishedAt);
}

/** Unread = visible releases published after the person last opened the page. */
export function unreadCount(visible: WhatsNewRelease[], lastSeenMs: number | null): number {
  if (lastSeenMs === null) return 0;
  return visible.filter((r) => r.publishedAt > lastSeenMs).length;
}

export function localized<T extends { en: ReleaseText; fr: ReleaseText | null }>(x: T, lang: string): ReleaseText {
  return lang.toLowerCase().startsWith('fr') && x.fr ? x.fr : x.en;
}

/** Router target for a highlight's "Try it" — a plain in-app path; the org URL sync puts it inside the open org. */
export function tryItTarget(h: Highlight): { commands: string[]; queryParams: Record<string, string> } | null {
  if (!h.tryIt || !TRY_IT_ROUTE.test(h.tryIt.route)) return null;
  return { commands: [h.tryIt.route], queryParams: h.tryIt.query };
}
